import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";

interface Project {
  title: string;
  description: string;
  metric: { value: string; label: string };
  outcomes: string[];
  tech: string[];
  githubUrl: string;
  category: string;
}

const projects: Project[] = [
  {
    title: "LLM Inference Service",
    description:
      "GPU-backed LLM inference service for low-latency, high-concurrency workloads. Production-grade reliability with 100+ concurrent users.",
    metric: { value: "180ms", label: "P50 latency" },
    outcomes: [
      "Reduced inference latency to ~180ms P50",
      "Supported 100+ concurrent requests",
      "Deployed on GPU cluster with vLLM optimization",
    ],
    tech: ["FastAPI", "vLLM", "CUDA", "Mistral-7B", "Python"],
    githubUrl: "https://github.com/Jeet-51",
    category: "AI Infrastructure",
  },
  {
    title: "PitchPal — AI Evaluator",
    description:
      "AI-powered startup evaluation platform using LangChain ReAct agents to automate investor due diligence workflows.",
    metric: { value: "60%", label: "faster diligence" },
    outcomes: [
      "Processed 100+ pitch deck evaluations",
      "Accelerated due diligence cycle by 60%",
    ],
    tech: ["LangChain", "GPT-4", "Streamlit", "Python"],
    githubUrl:
      "https://github.com/Jeet-51/PitchPal-AI-Agent-for-Startup-Pitch-Evaluation",
    category: "GenAI",
  },
  {
    title: "ClaimGuard",
    description:
      "Scalable ML pipeline forecasting Medicare billing behaviors using Bio_ClinicalBERT embeddings with full MLOps tracking.",
    metric: { value: "0.95", label: "R² accuracy" },
    outcomes: [
      "R² = 0.95 on unseen Medicare billing data",
      "Processed 200K+ claims",
      "MLflow + SHAP interpretability",
    ],
    tech: ["XGBoost", "PySpark", "MLflow", "SHAP", "Transformers"],
    githubUrl:
      "https://github.com/Jeet-51/ClaimGuard-Intelligent-Healthcare-Service-Pattern-Analysis",
    category: "ML Engineering",
  },
  {
    title: "EPA GHG Dashboard",
    description:
      "Production analytics stack with dbt models on Snowflake powering a Tableau dashboard for EPA greenhouse gas emissions analysis.",
    metric: { value: "40%", label: "less analysis time" },
    outcomes: ["Cut analyst query time by 40%", "Modular dbt models on Snowflake"],
    tech: ["Tableau", "dbt", "Snowflake", "SQL"],
    githubUrl: "https://github.com/Jeet-51/EPA-Greenhouse-Gas-GHG-Emissions-Dashboard",
    category: "Analytics",
  },
  {
    title: "FinanceFlow",
    description:
      "Real-time ETL pipeline on AWS using PySpark and SageMaker for fraud detection with end-to-end MLOps.",
    metric: { value: "+40%", label: "fraud detection" },
    outcomes: [
      "Boosted fraud detection accuracy by 40%",
      "Real-time pipeline: S3 → PySpark → SageMaker",
    ],
    tech: ["AWS", "S3", "SageMaker", "PySpark", "Python"],
    githubUrl:
      "https://github.com/Jeet-51/FinanceFlow-Cloud-Enabled-Analytics-for-Fraud-Detection",
    category: "Data Engineering",
  },
];

const ProjectTile = ({
  project,
  featured = false,
  delay = 0,
}: {
  project: Project;
  featured?: boolean;
  delay?: number;
}) => (
  <Reveal delay={delay} className={featured ? "md:col-span-2" : ""}>
    <a
      href={project.githubUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="panel group flex h-full flex-col p-6 transition-colors duration-300 hover:border-foreground/25 md:p-7"
    >
      <div className="mb-7 flex items-start justify-between gap-4">
        <span className="type-label text-muted-foreground/60">{project.category}</span>
        <ArrowUpRight className="h-4 w-4 flex-shrink-0 text-muted-foreground/40 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
      </div>

      {/* The metric is the hero of the tile */}
      <div className="mb-6">
        <p
          className={`type-metric text-foreground ${
            featured
              ? "text-[clamp(2.75rem,5.5vw,4rem)]"
              : "text-[clamp(2.25rem,4vw,2.75rem)]"
          }`}
        >
          {project.metric.value}
        </p>
        <p className="mt-2.5 type-label text-primary">{project.metric.label}</p>
      </div>

      <h3
        className={`font-display font-medium leading-snug text-foreground ${
          featured ? "text-2xl" : "text-lg"
        }`}
      >
        {project.title}
      </h3>

      <p className="mt-2.5 max-w-lg text-[0.9375rem] leading-relaxed text-muted-foreground">
        {project.description}
      </p>

      {featured && (
        <ul className="mt-6 space-y-2">
          {project.outcomes.map((o) => (
            <li
              key={o}
              className="relative pl-5 text-[0.9375rem] leading-relaxed text-muted-foreground before:absolute before:left-0 before:top-[0.6875em] before:h-1 before:w-1 before:rounded-full before:bg-muted-foreground/40"
            >
              {o}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto flex flex-wrap gap-1.5 pt-7">
        {project.tech.map((t) => (
          <span key={t} className="chip">
            {t}
          </span>
        ))}
      </div>
    </a>
  </Reveal>
);

const NewProjectsSection = () => (
  <>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <ProjectTile project={projects[0]} featured delay={0} />
      <ProjectTile project={projects[1]} delay={60} />
      <ProjectTile project={projects[2]} delay={0} />
      <ProjectTile project={projects[3]} delay={60} />
      <ProjectTile project={projects[4]} delay={120} />
    </div>

    <div className="mt-10">
      <a
        href="https://github.com/Jeet-51"
        target="_blank"
        rel="noopener noreferrer"
        className="link-underline inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        All repositories on GitHub
        <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
    </div>
  </>
);

export default NewProjectsSection;
